/**
 * Topbar — Modernize-style horizontal header.
 *
 * Design refresh Phase 8B. Rewritten from the old dark-glass Topbar
 * to a clean flat header matching the Modernize admin template:
 *
 *   - Mobile hamburger (reveals Sidebar)
 *   - Page title + optional breadcrumb (auto-generated from route)
 *   - Center search pill (opens CommandPalette on Cmd+K)
 *   - Theme toggle (light / dark / system cycle)
 *   - Notifications bell with unread badge
 *   - Avatar button with dropdown menu (profile / settings / logout)
 *
 * The previous ActivityRibbon component owned theme/notifications/user
 * menu popovers. Those are now first-class Topbar actions — there is
 * no second chrome surface. ActivityRibbon is no longer mounted; the
 * file remains on disk pending a final cleanup pass.
 */

import { useState, useRef, useEffect } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  Search,
  Menu,
  Bell,
  Sun,
  Moon,
  Monitor,
  LogOut,
  User as UserIcon,
  Settings,
  ChevronDown,
  Command,
  Sparkles,
  Shuffle,
  Zap,
  Presentation,
  EyeOff,
} from "lucide-react";
import { useSidebarStore } from "@/stores/sidebar";
import { usePresenting } from "@/stores/presenting";
import { useFelixPanelStore } from "@/stores/felixPanel";
import { useProjectStore } from "@/stores/project";
import { useAuthStore } from "@/stores/auth";
import { useTemplateLabels } from "@/hooks/useTemplateLabels";
import { useThemeStore, type ThemeMode } from "@/stores/theme";
import { useNotifications } from "@/api/hooks";
import { NotificationCenter } from "@/components/NotificationCenter";
import { authApi, projectsApi } from "@/api/services";
import { useQuery } from "@tanstack/react-query";
import { clearTokens } from "@/api/client";
import { cn } from "@/lib/utils";
import { TYPE } from "@/lib/typography";
import { recordTelemetry } from "@/lib/telemetry";
import { OrgSwitcher } from "@/components/layout/OrgSwitcher";
import { ActAsRoleSwitcher } from "@/components/layout/ActAsRoleSwitcher";
import { useFontSize } from "@/hooks/useFontSize";
import { crumbHasAPage } from "@/lib/sectionsWithoutAPage";

const ROUTE_LABELS: Record<string, string> = {
  projects: "Projects",
  objectives: "Objectives",
  "user-stories": "User Stories",
  personas: "Personas",
  components: "Components",
  tasks: "Tasks",
  tests: "Tests",
  defects: "Defects",
  documents: "Documents",
  traceability: "Traceability",
  baselines: "Baselines",
  "change-control": "Change Control",
  reports: "Reports",
  admin: "Admin",
  organization: "Organization",
  settings: "Settings",
  roles: "Roles",
  "audit-log": "Audit Log",
  meetings: "Meetings",
  "felix-runs": "Felix Runs",
  "qa-sessions": "Q&A Sessions",
  "action-items": "Action Items",
  "knowledge-base": "Knowledge Base",
  conversations: "Conversations",
  workspace: "Workspace",
  billing: "Billing",
  profile: "My Profile",
  team: "Team",
};

// Route slug → canonical entity-type slug, for the breadcrumb segments whose
// label is a determination-type WORD (and only those). Every other ROUTE_LABELS
// entry (traceability, baselines, change-control, reports, admin …) is a
// workflow/tool label, NOT a canonical entity type, so it is left verbatim.
// Each skinnable segment passes its ROUTE_LABELS word as the resolver fallback,
// so a project with no manifest renders byte-identical to today.
const ROUTE_SEGMENT_TO_CANONICAL: Record<string, string> = {
  objectives: "objective",
  "user-stories": "user_story",
  personas: "user_persona",
  components: "component",
  tasks: "impl_task",
  tests: "test_case",
  defects: "defect",
  documents: "document",
};

const THEME_ICON: Record<ThemeMode, typeof Sun> = {
  light: Sun,
  dark: Moon,
  system: Monitor,
};

const THEME_NEXT: Record<ThemeMode, ThemeMode> = {
  light: "dark",
  dark: "system",
  system: "light",
};

const THEME_LABEL: Record<ThemeMode, string> = {
  light: "Switch to dark mode",
  dark: "Switch to system theme",
  system: "Switch to light mode",
};

interface Crumb {
  label: string;
  path: string;
  isLast: boolean;
}

/** A path segment that is a record's id (a UUID), not a page name. The
 *  breadcrumb leaves it out: the lobby read "Home / Meeting / B63934cc 4006 4a7f
 *  8aa1 Cc9dcd / Lobby" (row 370236ca, production 2026-10-12). The project's
 *  own id is not dropped; it is shown as the project's name. */
export function isRawIdSegment(seg: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg);
}

function useBreadcrumbs(): Crumb[] {
  const { pathname } = useLocation();
  const { projectId } = useParams<{ projectId: string }>();
  const projectName = useProjectStore((s) => s.selectedProjectName);
  // Template-layer SKIN: relabel the entity-type breadcrumb segments to the
  // project's manifest vocabulary. Disabled (identity) when not in a project.
  const { label: skinLabel } = useTemplateLabels(projectId ?? null);
  // The store holds the name only when the project was picked from a list; a
  // page opened by link or by ⌘K read "Projects / Project / Requirements"
  // (founder 6427360275 item 7). Read it then, on the cache key useProject uses.
  const fetchedName = useQuery({
    queryKey: ["projects", "detail", projectId ?? ""],
    queryFn: () => projectsApi.get(projectId!),
    enabled: !!projectId && !projectName,
    staleTime: 60_000,
    retry: false,
  }).data?.name;

  if (pathname === "/") return [];

  const segments = pathname.split("/").filter(Boolean);
  const out: Crumb[] = [];
  let path = "";
  segments.forEach((seg) => {
    path += `/${seg}`;
    if (seg !== projectId && isRawIdSegment(seg)) return;
    let label: string;
    if (projectId && seg === projectId) {
      label = projectName || fetchedName || "Project";
    } else {
      const fallback =
        ROUTE_LABELS[seg] ??
        seg
          .split("-")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ");
      const canonical = ROUTE_SEGMENT_TO_CANONICAL[seg];
      // Only entity-type segments (and only inside a project) get skinned;
      // the fallback keeps a non-template project byte-identical.
      label =
        canonical && projectId
          ? skinLabel(canonical, { plural: true, fallback })
          : fallback;
    }
    out.push({ label, path, isLast: false });
  });
  if (out.length) out[out.length - 1].isLast = true;
  return out;
}

function openCommandPalette() {
  window.dispatchEvent(
    new KeyboardEvent("keydown", { key: "k", metaKey: true }),
  );
}

// ---------------------------------------------------------------------------
// User menu
// ---------------------------------------------------------------------------

function UserMenu() {
  const user = useAuthStore((s) => s.user);
  const clearUser = useAuthStore((s) => s.clearUser);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // Presentation size (row 4bd890ab): one click for a shared screen. The same
  // setting as Profile -> Appearance -> Display -> Font Size.
  const [fontSize, setFontSize] = useFontSize();
  const presenting = fontSize === "presentation";

  useEffect(() => {
    function click(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", click);
    return () => document.removeEventListener("mousedown", click);
  }, []);

  const displayName = user?.name ?? "User";
  const initials =
    displayName
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "U";

  async function handleLogout() {
    try {
      await authApi.logout();
    } catch {
      /* ignore */
    }
    clearTokens();
    clearUser();
    navigate("/login");
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          "flex items-center gap-2 rounded-xl p-1 pl-1 pr-2 transition-colors",
          "hover:bg-[var(--color-surface-secondary)]",
          open && "bg-[var(--color-surface-secondary)]",
        )}
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-brand-600 text-xs font-semibold text-white ring-2 ring-white shadow-sm dark:ring-slate-900">
          {initials}
        </div>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 text-[var(--color-text-tertiary)] transition-transform",
            open && "rotate-180",
          )}
        />
      </button>

      {open && (
        <div
          role="menu"
          className={cn(
            "absolute right-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-xl",
            "border border-[var(--color-border)] bg-[var(--color-surface)] shadow-lg",
          )}
        >
          {/* Header */}
          <div className="border-b border-[var(--color-border)] px-4 py-3">
            <div className={cn("truncate text-[var(--color-text-primary)]", TYPE.bodyMed)}>
              {displayName}
            </div>
            {user?.email && (
              <div className={cn("mt-0.5 truncate text-[var(--color-text-tertiary)]", TYPE.meta)}>
                {user.email}
              </div>
            )}
          </div>

          {/* Items */}
          <div className="py-1">
            <Link
              to="/profile"
              onClick={() => setOpen(false)}
              className={cn("flex items-center gap-3 px-4 py-2 text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-surface-secondary)]", TYPE.body)}
              role="menuitem"
            >
              <UserIcon className="h-4 w-4 text-[var(--color-text-tertiary)]" />
              My Profile
            </Link>
            <Link
              to="/organization/settings"
              onClick={() => setOpen(false)}
              className={cn("flex items-center gap-3 px-4 py-2 text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-surface-secondary)]", TYPE.body)}
              role="menuitem"
            >
              <Settings className="h-4 w-4 text-[var(--color-text-tertiary)]" />
              Organization Settings
            </Link>
            <button
              type="button"
              role="menuitemcheckbox"
              aria-checked={presenting}
              data-testid="presentation-size-toggle"
              onClick={() => setFontSize(presenting ? "normal" : "presentation")}
              className={cn("flex w-full items-center gap-3 px-4 py-2 text-left text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-surface-secondary)]", TYPE.body)}
            >
              <Presentation className="h-4 w-4 text-[var(--color-text-tertiary)]" />
              <span className="flex-1">Presentation size</span>
              <span
                aria-hidden
                className={cn(
                  "relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors",
                  presenting ? "bg-brand-600" : "bg-[var(--color-border-hover,#cbd5e1)]",
                )}
              >
                <span
                  className={cn(
                    "absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform",
                    presenting ? "translate-x-4" : "translate-x-0.5",
                  )}
                />
              </span>
            </button>
          </div>

          <div className="border-t border-[var(--color-border)] py-1">
            <button
              onClick={handleLogout}
              className={cn("flex w-full items-center gap-3 px-4 py-2 text-left text-rose-600 transition-colors hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-900/20", TYPE.body)}
              role="menuitem"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Theme toggle
// ---------------------------------------------------------------------------

function ThemeToggle() {
  const { mode, setMode } = useThemeStore();
  const Icon = THEME_ICON[mode];
  return (
    <button
      onClick={() => setMode(THEME_NEXT[mode])}
      className="flex h-9 w-9 items-center justify-center rounded-xl text-[var(--color-text-tertiary)] transition-colors hover:bg-[var(--color-surface-secondary)] hover:text-[var(--color-text-secondary)]"
      title={THEME_LABEL[mode]}
      aria-label={THEME_LABEL[mode]}
    >
      <Icon className="h-[18px] w-[18px]" />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Presenting toggle
// ---------------------------------------------------------------------------

/** Hides internal costs and margins while the screen is shared (stores/presenting). */
export function PresentingToggle() {
  const presenting = usePresenting((s) => s.presenting);
  const toggle = usePresenting((s) => s.toggle);
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label="Presenting"
      aria-pressed={presenting}
      title="Hides internal costs and margins while you share your screen"
      data-testid="topbar-presenting"
      className={cn(
        TYPE.bodyMed,
        "flex h-9 items-center gap-1.5 rounded-xl px-2.5 transition-colors",
        presenting
          ? "bg-brand-50 text-brand-700 dark:bg-brand-900/30"
          // The secondary text token, not the tertiary one: tertiary (#64748b) on the top bar's
          // tint measured 4.21:1, under WCAG AA's 4.5:1, and failed the nightly axe scan every
          // night (test_axe_scan_felix_panel_open, 28 Oct to 1 Nov). Secondary is 9.17:1 there.
          : "text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-secondary)] hover:text-[var(--color-text-primary)]",
      )}
    >
      <EyeOff className="h-4 w-4" />
      <span className="hidden md:inline">Presenting</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Notifications bell
// ---------------------------------------------------------------------------

function NotificationsButton() {
  const [open, setOpen] = useState(false);
  const { data } = useNotifications({ limit: 5, unread_only: true });
  // While the screen is shared the count is hidden (founder 6427360275 item
  // 10: "Notifications (1369 unread)" on a client's screen); the bell stays.
  const presenting = usePresenting((s) => s.presenting);
  const unread = presenting ? 0 : data?.total ?? 0;
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="relative flex h-9 w-9 items-center justify-center rounded-xl text-[var(--color-text-tertiary)] transition-colors hover:bg-[var(--color-surface-secondary)] hover:text-[var(--color-text-secondary)]"
        aria-label={`Notifications${unread > 0 ? ` (${unread} unread)` : ""}`}
      >
        <Bell className="h-[18px] w-[18px]" />
        {unread > 0 && (
          <span
            className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white shadow-sm ring-2 ring-white dark:ring-slate-900"
            aria-hidden
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      <NotificationCenter open={open} onClose={() => setOpen(false)} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Topbar
// ---------------------------------------------------------------------------

export function Topbar() {
  const navigate = useNavigate();
  const setMobileOpen = useSidebarStore((s) => s.setMobileOpen);
  const felixMode = useSidebarStore((s) => s.felixMode);
  const felixOpen = useFelixPanelStore((s) => s.open);
  const felixWidth = useFelixPanelStore((s) => s.width);
  const breadcrumbs = useBreadcrumbs();
  // useParams, not the project store: the store's selected id survives leaving
  // a project, so it would keep offering "Change mode" on /settings.
  const { projectId } = useParams<{ projectId: string }>();
  // Phase 7 (UX v2) — the "Ask Felix" pill is the canonical desktop
  // Felix entry point. The legacy bottom-right FelixLauncher FAB has
  // been removed from AppShell. Plan §Phase 7 ratified decision: full
  // demotion — chat is an escape-hatch, not a primary nav surface.
  // No flag gate — one entry point, always rendered on lg+ screens.
  const toggleFelix = useFelixPanelStore((s) => s.toggle);
  // Pulled from useLocation for the felix_pill_click telemetry payload.
  // useBreadcrumbs() also reads pathname but its hook scope can't leak
  // here.
  const { pathname: currentPath } = useLocation();

  // KI-0195: left offset must clear both sidebar AND Felix panel so the
  // topbar header starts at the main content's left edge, not inside
  // Felix's panel range.  Previously used just `${felixWidth}px` which
  // left a 304px zone where the topbar header overlapped Felix.
  // Iterator runs edge-to-edge (no left nav), so the topbar starts at 0 there —
  // otherwise it leaves an empty --sidebar-width gap on the left.
  const immersive = currentPath.endsWith("/iterator");
  const leftOffset = immersive
    ? "0px"
    : felixMode && felixOpen
      ? `calc(var(--sidebar-width) + ${felixWidth}px)`
      : "var(--sidebar-width)";

  const topbarStyle = {
    "--desktop-left": leftOffset,
  } as React.CSSProperties;

  const pageTitle =
    breadcrumbs.length > 0
      ? breadcrumbs[breadcrumbs.length - 1].label
      : "Dashboard";

  return (
    <header
      className={cn(
        "fixed top-0 right-0 z-30 h-[var(--topbar-height)]",
        "border-b border-[var(--color-border)] bg-[var(--color-surface)]/95 backdrop-blur-sm",
        "topbar-left-offset transition-all duration-300",
      )}
      style={topbarStyle}
    >
      <div className="flex h-full items-center justify-between gap-3 px-3 sm:px-6">
        {/* Left: hamburger + title / breadcrumbs */}
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <button
            onClick={() => setMobileOpen(true)}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-surface-secondary)] lg:hidden"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="min-w-0 flex-1">
            <div
              className="truncate text-sm font-semibold text-[var(--color-text-primary)] sm:text-base"
              title={typeof pageTitle === "string" ? pageTitle : undefined}
            >
              {pageTitle}
            </div>
            <nav
              className="hidden min-w-0 items-center gap-1 overflow-hidden whitespace-nowrap text-[11px] text-[var(--color-text-tertiary)] sm:flex"
              aria-label="Breadcrumb"
            >
              <Link
                to="/"
                className="transition-colors hover:text-[var(--color-text-secondary)]"
              >
                Home
              </Link>
              {breadcrumbs.length > 1 && (
                <>
                  {breadcrumbs.slice(0, -1).map((c) => (
                    <span key={c.path} className="flex shrink-0 items-center gap-1">
                      <span className="text-[var(--color-text-tertiary)]">/</span>
                      {/* A section with no page of its own is a label, not a link (row 42d67c26). */}
                      {crumbHasAPage(c.path) ? (
                        <Link
                          to={c.path}
                          className="transition-colors hover:text-[var(--color-text-secondary)]"
                        >
                          {c.label}
                        </Link>
                      ) : (
                        <span data-testid="breadcrumb-section">{c.label}</span>
                      )}
                    </span>
                  ))}
                  <span className="flex min-w-0 items-center gap-1">
                    <span className="text-[var(--color-text-tertiary)]">/</span>
                    <span
                      className="truncate text-[var(--color-text-secondary)]"
                      data-testid="breadcrumb-current"
                    >
                      {breadcrumbs[breadcrumbs.length - 1].label}
                    </span>
                  </span>
                </>
              )}
            </nav>
          </div>
        </div>

        {/* Center: search — shows at sm+ to match where mobile search hides */}
        <button
          onClick={openCommandPalette}
          className={cn(
            TYPE.body,
            "hidden items-center gap-2 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-secondary)] px-3 py-1.5 text-[var(--color-text-tertiary)] transition-colors sm:flex",
            "hover:bg-[var(--color-surface-tertiary)] hover:border-[var(--color-border-hover)] hover:text-[var(--color-text-secondary)]",
            "min-w-[160px] md:min-w-[220px] lg:min-w-[260px]",
          )}
        >
          <Search className="h-4 w-4 shrink-0" />
          <span className="flex-1 text-left">Search…</span>
          <kbd className="hidden items-center gap-0.5 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--color-text-tertiary)] lg:inline-flex">
            <Command className="h-2.5 w-2.5" />K
          </kbd>
        </button>

        {/* Right: actions */}
        <div className="flex shrink-0 items-center gap-1">
          {/* CHANGE HOW YOU WORK ON THIS PROJECT (row 4b7012f6).
              Remembering the mode made the picker unreachable: the bare project
              URL now redirects to the remembered mode, and each mode lands on a
              route of its own with no way back — so the first choice was
              permanent. This is the way back, and it lives in the Topbar
              because that is the ONE surface present in every mode, including
              Iterate/Operate/Broadcast where the sidebar is suppressed
              deliberately. */}
          {projectId && (
            <Link
              to={`/projects/${projectId}?pick=1`}
              className="hidden h-9 items-center gap-1.5 rounded-xl px-2.5 text-xs font-medium text-[var(--color-text-tertiary)] transition-colors hover:bg-[var(--color-surface-secondary)] hover:text-[var(--color-text-secondary)] md:inline-flex"
              title="Change how you work on this project"
              data-testid="topbar-change-mode"
              aria-label="Change mode"
            >
              <Shuffle className="h-3.5 w-3.5" />
              {/* Icon only below 1800px: at 1600px the label pushed the page
                  title to "Keystone Valley OnBase …" (row 8f61634b). */}
              <span className="hidden min-[1800px]:inline">Change mode</span>
            </Link>
          )}
          {/* Org switcher — only renders for users in more than one org */}
          <OrgSwitcher />
          {/* Act-as role — lets a tester run our role-gated manual cases.
              Only renders when they can assume more than their own role. */}
          <ActAsRoleSwitcher />
          {/* Mobile search button */}
          <button
            onClick={openCommandPalette}
            className="flex h-9 w-9 items-center justify-center rounded-xl text-[var(--color-text-tertiary)] transition-colors hover:bg-[var(--color-surface-secondary)] hover:text-[var(--color-text-secondary)] sm:hidden"
            aria-label="Search"
          >
            <Search className="h-[18px] w-[18px]" />
          </button>
          {/* Global "Meet Now" (Bob 2026-06-07: "where is the meet now
              function?!"). The instant-meeting dialog lives on the
              Communications page; previously the ONLY entry was that page's
              toolbar, so it was undiscoverable from anywhere else. This
              top-bar action navigates to /calendar?meet=now; the Communications
              page reads that param and auto-opens the QuickMeetingDialog. */}
          <button
            type="button"
            onClick={() => {
              recordTelemetry("ux.behavior.click", {
                path: "/topbar",
                role: "button",
                target: "meet-now",
                felix_session: false,
              });
              navigate("/calendar?meet=now");
            }}
            aria-label="Meet Now"
            title="Start an instant meeting"
            data-testid="topbar-meet-now"
            className={cn(
              TYPE.bodyMed,
              "hidden items-center gap-1.5 rounded-xl px-3 py-1.5 sm:flex",
              "border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text-secondary)] transition-colors",
              "hover:bg-[var(--color-surface-secondary)] hover:border-[var(--color-border-hover)]",
            )}
          >
            <Zap className="h-3.5 w-3.5 text-amber-500" />
            <span>Meet Now</span>
          </button>
          {/* Phase 7 (UX v2): "Ask Felix" pill — the canonical desktop
              Felix entry point. No flag gate; the legacy bottom-right
              FelixLauncher FAB has been removed from AppShell. One
              entry per device size — pill on lg+, MobileBottomNav
              tab below. */}
          <button
            type="button"
            onClick={() => {
              // Phase 7 telemetry — engagement signal for the
              // cohort-rollout dashboards (see
              // docs/UX_V2_FELIX_DEMOTION_PLAYBOOK.md §Telemetry).
              recordTelemetry("felix_pill_click", {
                panel_open: felixOpen,
                from_route: currentPath,
              });
              toggleFelix();
            }}
            aria-label={felixOpen ? "Close Felix" : "Ask Felix"}
            aria-pressed={felixOpen}
            title="Ask Felix"
            data-testid="ask-felix-pill"
            className={cn(
              TYPE.bodyMed,
              "hidden lg:flex items-center gap-1.5 rounded-xl px-3 py-1.5",
              "border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text-secondary)] transition-colors",
              "hover:bg-[var(--color-surface-secondary)] hover:border-[var(--color-border-hover)]",
              felixOpen && "bg-brand-50 border-brand-200 text-brand-700 dark:bg-brand-900/30",
            )}
          >
            <Sparkles className="h-3.5 w-3.5" />
            <span>Ask Felix</span>
          </button>
          {/* The language picker was WITHDRAWN (row 27e0706c): it implied a
              localisation that did not exist — 52 strings across 4 of roughly
              500 components. Formatting is unaffected and still follows the
              browser, deliberately separated from the UI translation language
              in i18n/format.ts. Guarded by
              __tests__/theTopbarOffersNoLanguagePicker.test.ts, because a
              removal leaves nothing to mutate and would otherwise be
              reintroduced with no build going red. */}
          {/* FrontierToggle removed (T3 one-home collapse, 2026-07-11):
              the briefing-first landing is THE home for all users, so the
              Classic/Frontier posture switch retired. stores/uiMode.ts
              survives hard-defaulted to "frontier" for the surfaces that
              consult it for lead strips. */}
          <PresentingToggle />
          <ThemeToggle />
          <NotificationsButton />
          <div className="ml-1 border-l border-[var(--color-border)] pl-2">
            <UserMenu />
          </div>
        </div>
      </div>
    </header>
  );
}
